<template>
  <b-modal
    ref="synopticModal"
    size="xxl"
    :title="$tc('global.description.synopticTemplate')"
    centered
    modal-class="synoptic-modal"
    qa="synoptic-templates-modal"
    @show="init()"
  >
    <!--Table view: item selection-->

    <b-row v-if="!hideTemplatesList">
      <b-col class="filters-header bottom-bordered pb-2">
        <b-form-input
          v-model="titleFilter"
          qa="synoptic-templates-modal-filter-input"
          type="search"
          :placeholder="$t('diagnosis.labels.filterByTitleAndDescription')"
          class="filter-input form-control-sm"
        />
      </b-col>
      <b-col
        v-if="compatibleSwitchVisible"
        qa="synoptic-templates-modal-checkbox"
        cols="auto"
        class="filters-header mt-1"
      >
        <b-form-checkbox
          v-model="customConfig.showOnlyCompatible"
          name="checkbox-text"
        >
          <span class="dark">{{ $t('global.description.modal.compatible') }}</span>
        </b-form-checkbox>
      </b-col>
    </b-row>
    <div
      class="synoptic-templates-table full-bordered"
      qa="synoptic-templates-modal-table"
    >
      <b-container fluid>
        <b-row
          v-if="!hideTemplatesList"
          class="table-header component-inside-1 full-bordered d-flex align-items-center"
        >
          <b-col
            cols="3"
            class="label-dark"
          >
            {{ $t('diagnosis.labels.title') }}
          </b-col>
          <b-col class="label-dark">
            {{ $t('diagnosis.labels.description') }}
          </b-col>
        </b-row>
        <b-row class="table-content left-bordered right-bordered bottom-bordered">
          <b-col>
            <b-row
              v-for="template in filteredTemplates"
              :key="template.id"
              qa="synoptic-templates-modal-table-row"
              class="table-row bottom-bordered py-3 px-2"
              :class="{'row-active': selectedTemplateId === template.id}"
              @click="selectTemplate(template.id)"
            >
              <b-col
                class="table-row-tittle label-dark"
                qa="synoptic-templates-modal-table-row-title"
                cols="3"
              >
                {{ template.title }}
              </b-col>
              <b-col
                class="table-row-desc label-dark"
                qa="synoptic-templates-modal-table-row-description"
              >
                <div v-html="template.text" />
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </b-container>
      <!--Template Edition View-->
      <div
        v-if="selectedTemplate"
        class="panel-visible synoptic active"
        :class="{ 'w-100': hideTemplatesList }"
      >
        <div class="template-sidepanel ap-sidepanel">
          <div class="header bottom-bordered px-4 py-2 d-flex align-items-center justify-content-between">
            <span
              class="ap-title ap-title__main"
              qa="synoptic-templates-modal-sidepanel-title"
            >{{ selectedTemplate.title }}</span>
            <div v-if="!hideTemplatesList">
              <b-button
                v-tooltip="$t('global.description.preview')"
                class="btn__icon btn__icon--primary px-2"
                qa="synoptic-templates-modal-sidepanel-preview-button"
                @click="togglePreviewMode()"
              >
                <i
                  aria-hidden="true"
                  :class="!previewMode ? 'far fa-eye' : 'far fa-eye-slash'"
                />
              </b-button>
              <b-button
                v-tooltip="$t('global.description.externalDocumentation')"
                :disabled="!hasExternalDocumentation"
                class="btn__icon btn__icon--primary px-2"
                qa="synoptic-templates-modal-sidepanel-external-button"
                @click="openExternalDocumentation"
              >
                <i
                  aria-hidden="true"
                  class="far fa-book"
                />
              </b-button>
              <b-button
                v-tooltip="$tc('global.configureSynopticTemplate')"
                class="btn__icon btn__icon--primary px-2"
                qa="synoptic-templates-modal-sidepanel-configuration-button"
                @click="configureSynopticTemplateClick"
              >
                <i
                  aria-hidden="true"
                  class="far fa-cog"
                />
              </b-button>
              <b-button
                class="btn__icon btn__icon--primary ml-2"
                qa="synoptic-templates-modal-sidepanel-close-button"
                @click="selectedTemplate = null"
              >
                <i
                  aria-hidden="true"
                  class="far fa-times"
                />
              </b-button>
            </div>
          </div>
          <div>
            <b-row class="px-4 py-3">
              <b-col>
                <vt-input
                  v-model="treeFilter"
                  class="input-type-filter component-bg"
                  name="synoptic-search"
                  :placeholder="$t('global.filter')"
                  size="sm"
                />
              </b-col>
            </b-row>
          </div>
          <div v-if="previewMode">
            <div
              class="m-4"
              v-html="generateTextHtml()"
            />
          </div>
          <div
            v-else-if="!previewMode && !!treeModel"
            class="text-edition pt-3 px-3 pb-4"
            :class="{'options-not-filled': fieldValidation }"
          >
            <liquor-tree
              id="nodeTree"
              ref="tree"
              :filter="treeFilter"
              :options="treeOptions"
              @tree:filtered="onTreeFiltered($event)"
              @tree:mounted="onTreeMounted($event)"
            >
              <div
                slot-scope="{ node }"
                class="w-100"
              >
                <div
                  :id="`${node.data.code}node`"
                  class="w-100"
                  :class="{ 'invalid' : !node.data.valid }"
                >
                  <div class="d-flex">
                    <b-form-radio-group
                      v-if="singleSelection(node)"
                      v-model="node.data.checked"
                      class="radio-button mb-2"
                    >
                      <b-form-radio
                        :id="node.id"
                        :value="true"
                        @click.native.prevent="setSingleSelectionNode(node)"
                      >
                        <label
                          :class="{ 'primary-color' : isSelectableNode(node) && node.data.checked }"
                          class="mr-2"
                        >{{ node.text }}<i
                          v-if="node.data.required"
                          class="required-field ml-1"
                          aria-hidden="true"
                        >*</i></label>
                      </b-form-radio>
                    </b-form-radio-group>
                    <b-form-checkbox
                      v-else-if="multipleSelection(node)"
                      :id="node.id"
                      v-model="node.data.checked"
                      class="mb-2"
                      :value="true"
                      @change="setMultipleSelectionNode(node)"
                    />
                    <label
                      v-if="!singleSelection(node)"
                      :class="{ 'primary-color' : isSelectableNode(node) && node.data.checked }"
                      class="mr-2"
                    >{{ node.text }}<i
                      v-if="node.data.required"
                      class="required-field ml-1"
                      aria-hidden="true"
                    >*</i></label>
                  </div>
                  <vt-input
                    v-if="node.data.nodeType === CommonSynopticTypeOptions.VARIABLE_TEXT"
                    :ref="node.data.code"
                    v-model="node.data.value"
                    :placeholder="$t('global.description.textPlaceholder')"
                    :name="node.data.code"
                    class="mr-2 form-control-sm"
                    @input="setNode(node)"
                  />
                  <vt-input
                    v-else-if="node.data.nodeType === CommonSynopticTypeOptions.VARIABLE_NUMBER"
                    :ref="node.data.code"
                    v-model="node.data.value"
                    :placeholder="$t('global.description.numberPlaceholder')"
                    :name="node.data.code"
                    type="number"
                    class="mr-2 form-control-sm synoptic-modal-number"
                    @input="setNode(node)"
                  />
                  <div
                    v-else-if="node.data.nodeType === CommonSynopticTypeOptions.VARIABLE_PERCENTAGE"
                    class="d-flex align-items-center"
                  >
                    <b-form-input
                      id="range-1"
                      :ref="node.data.code"
                      v-model="node.data.value"
                      type="range"
                      min="0"
                      max="100"
                      @input="setNode(node)"
                    />
                    <div class="ml-3">
                      <span>{{ node.data.value }}%</span>
                    </div>
                  </div>
                  <vt-date-picker
                    v-else-if="node.data.nodeType === CommonSynopticTypeOptions.VARIABLE_DATE"
                    :ref="node.data.code"
                    class="synoptic-modal-date-picker"
                    type="date"
                    :model="node.data.value"
                    :not-after="new Date()"
                    @update="node.data.value = $event, setNode(node)"
                    @clear="setNode(node)"
                  />
                  <div
                    v-else-if="node.data.nodeType === CommonSynopticTypeOptions.VARIABLE_IMAGE"
                    :key="refreshImageNode"
                    class=""
                  >
                    <b-button
                      size="sm"
                      class="btn__outline browse-btn"
                      @click="$emit('openAssociatedImagesModal', node.data.code)"
                    >
                      <p class="p-0 m-0">
                        <i
                          aria-hidden="true"
                          class="fal fa-folder-open mr-2"
                        />{{ $t('component.studyRequest.addFileModal.exploreButton') }}
                      </p>
                    </b-button>
                    <span
                      v-if="node.data.filename"
                      class="ml-2"
                    >{{ node.data.filename }}</span>
                    <span
                      v-else
                      class="synoptic-modal-picture-placeholder ml-2"
                    >{{ $t('global.description.picturePlaceholder') }}</span>
                  </div>
                </div>
              </div>
            </liquor-tree>
          </div>
          <div
            :class="!hideTemplatesList ? 'justify-content-between' : 'justify-content-end'"
            class="footer top-bordered p-2 d-flex"
          >
            <b-button
              v-if="!hideTemplatesList"
              size="sm"
              variant="outline-secondary"
              qa="synoptic-templates-modal-sidepanel-close-footer-button"
              @click="selectedTemplate=false"
            >
              {{ $t('global.close') }}
            </b-button>
            <div>
              <b-button
                size="sm"
                class="btn--primary"
                qa="synoptic-templates-modal-sidepanel-apply-button"
                @click="applyTemplateAndClose"
              >
                <i
                  class="fal fa-plus mr-2"
                  aria-hidden="true"
                />
                {{ $t('global.apply') }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      slot="modal-footer"
      class="footer-banner px-2"
      style="width:100%;"
    >
      <div class="row">
        <div class="col-6 text-left">
          <b-button
            size="sm"
            variant="outline-secondary"
            data-dismiss="modal"
            @click="hide"
          >
            {{ $t('global.cancel') }}
          </b-button>
        </div>
        <div class="col-6 text-right" />
      </div>
    </div>
  </b-modal>
</template>

<script>
import { commonSynopticTemplateAttachmentService } from '@/api/resources/common/synoptic-template-services/synoptic-template-attachment.service'
import { commonSynopticTemplateService } from '@/api/resources/common/synoptic-template-services/synoptic-template.service'
import { CommonSynopticTypeOptions, CommonSynopticTypeChildSelection } from '@/api/resources/common/synoptic-template-services/synoptic-template.model'
import customConfigResource from '@/api/resources/ui/CustomConfigResource'
import { cloneDeep, isEmpty } from 'lodash'
import LiquorTree from 'liquor-tree'
import { DescriptionType } from '@/api/resources/description/description.model'
import i18n from '@/lang/i18n'
import { TextNormalization } from '@/shared/util/text-normalization'

const SYNOPTIC_TEMPLATES_UPDATE_CHANNEL = 'synopticTemplatesUpdateChannel'

export default {
    name: 'SynopticTemplatesModal',
    components: { LiquorTree },
    props: {
        laboratoryId: { type: Number, required: true },
        descriptionLaboratoryId: { type: Number, required: false },
        caseId: { type: Number, required: false },
        sampleId: { type: Number, required: false },
        templateIdInUse: { type: Number, required: false },
        optionsSelectedForTemplateInUse: { type: Object, required: false },
        uiComponentCode: { type: String, required: false },
        avoidValidations: { type: Boolean, default: false }
    },
    data() {
        return {
            refreshImageNode: 0,
            treeOptions: {
                autoCheckChildren: false,
                keyboardNavigation: false,
                multiple: false,
                parentSelect: true,
                filter: {
                    emptyText: i18n.tc('global.noResults'),
                    matcher(query, node) {
                        return node.data.text.toLowerCase().includes(query.toLowerCase()) ||
                            node.data.code.toLowerCase().includes(query.toLowerCase())
                    }
                }
            },
            tree: null,
            treeModel: null,
            savedTreeModel: null,
            treeFilter: '',
            radioParentSelected: false,
            previewMode: false,
            selectedTemplate: false,
            titleFilter: '',
            templates: [],
            selectedTemplateId: null,
            customConfig: {
                showOnlyCompatible: true
            },
            fieldValidation: false,
            selectedOptions: {},
            loadCurrentTemplateDetails: true,
            foundNodes: 0,
            totalNodes: 0,
            totalFilteredNodeCodes: [],
            currentSearchIndex: 0,
            templateExternalDocumentation: {
                hasExternalDocumentation: false,
                file: null,
                contentType: null
            },
            broadcastUpdateSynopticTemplatesChannel: null
        }
    },
    computed: {
        hideTemplatesList() {
            return this.loadCurrentTemplateDetails && this.descriptionLaboratoryId
                ? this.laboratoryId !== this.descriptionLaboratoryId : false
        },
        CommonSynopticTypeOptions() {
            return CommonSynopticTypeOptions
        },
        CommonSynopticTypeChildSelection() {
            return CommonSynopticTypeChildSelection
        },
        compatibleSwitchVisible() {
            return this.caseId && this.sampleId && this.uiComponentCode
        },
        filteredTemplates() {
            const normalizedInputTokens = TextNormalization.removeDiacritics(this.titleFilter).split(' ')
            return this.templates.filter(template => template.active && (
                normalizedInputTokens.reduce((accumulator, normalizedInput) => {
                    return accumulator && (
                        TextNormalization.removeDiacritics(template.title).includes(normalizedInput))
                }, true)
            ))
        },
        applyButtonText() {
            return this.selectedTemplate ? this.$t('global.description.modal.apply') : ''
        },
        applyCloseButtonText() {
            return this.selectedTemplate ? this.$t('global.description.modal.applyClose') : ''
        },
        hasExternalDocumentation() {
            return this.templateExternalDocumentation.hasExternalDocumentation
        }
    },
    watch: {
        'customConfig': {
            handler(newConfig) {
                if (this.compatibleSwitchVisible) {
                    customConfigResource.save(this.uiComponentCode, newConfig)
                }
            },
            deep: true
        },
        'customConfig.showOnlyCompatible': function() {
            this._loadTemplates()
        }
    },
    async created() {
        if (this.compatibleSwitchVisible) {
            const customConfig = await customConfigResource.get(this.uiComponentCode)
            if (customConfig) {
                this.customConfig = customConfig.config
            }
        }
    },
    beforeDestroy() {
        this.broadcastUpdateSynopticTemplatesChannel?.close()
    },
    methods: {
        async show(loadCurrentTemplateDetails = true) {
            this.setBroadcastUpdateSynopticTemplatesChannel()
            this.loadCurrentTemplateDetails = loadCurrentTemplateDetails
            if (!this.loadCurrentTemplateDetails) this.cleanSelected()
            if (this.compatibleSwitchVisible) {
                const customConfig = await customConfigResource.get(this.uiComponentCode)
                if (customConfig) {
                    this.customConfig = customConfig.config
                }
            }
            this.$refs.synopticModal.show()
        },
        hide() {
            this.broadcastUpdateSynopticTemplatesChannel?.close()
            this.$refs.synopticModal.hide()
        },
        cleanSelected() {
            this.selectedTemplateId = null
            this.selectedTemplate = null
            this.selectedOptions = {}
        },
        configureSynopticTemplateClick() {
            const routeData = this.$router.resolve({ name: 'synopticTemplates', params: { id: this.selectedTemplateId } })
            window.open(routeData.href, '_blank')
        },
        setBroadcastUpdateSynopticTemplatesChannel() {
            this.broadcastUpdateSynopticTemplatesChannel = new BroadcastChannel(SYNOPTIC_TEMPLATES_UPDATE_CHANNEL)
            this.broadcastUpdateSynopticTemplatesChannel.onmessage = async () => {
                await this._loadTemplates()
                await this._loadTemplateDetails()
            }
        },

        _generateIndentation(node) {
            let spaces = ''
            for (let i = 0; i < node.depth * 10; i++) {
                spaces += '&nbsp;'
            }
            return spaces
        },
        _generate(node) {
            let textHtml = ''
            if (node.data.checked) {
                let value = node.data.value ?? ''
                if (node.data.nodeType === this.CommonSynopticTypeOptions.VARIABLE_IMAGE) {
                    value = `<span>${node.data.filename}</span>`
                } else if (node.data.nodeType === this.CommonSynopticTypeOptions.VARIABLE_DATE) {
                    value = node.data.value.toLocaleDateString()
                } else if (node.data.nodeType === this.CommonSynopticTypeOptions.VARIABLE_PERCENTAGE) {
                    value = `${node.data.value}%`
                }

                const spaces = this._generateIndentation(node)
                if (node.hasChildren() && node.data.nodeType === CommonSynopticTypeOptions.TITLE) {
                    textHtml += `<p><strong>${spaces}${node.data.text} ${value}</strong></p>`
                } else {
                    textHtml += `<p>${spaces}${node.data.text} ${value}</p>`
                }
            }
            return textHtml
        },
        generateTextHtml() {
            let textHtml = ''
            const allNodes = this.tree?.findAll({}) ?? []
            allNodes.forEach(node => (textHtml += this._generate(node)))
            return textHtml
        },

        togglePreviewMode() {
            if (!this.previewMode) {
                this.savedTreeModel = this.tree?.model
            }
            this.previewMode = !this.previewMode
        },
        loadCustomOptionsFromParams(tree) {
            if (this.selectedTemplate && this.templateIdInUse &&
                this.selectedTemplate.id === this.templateIdInUse &&
                this.optionsSelectedForTemplateInUse) {
                Object.keys(this.optionsSelectedForTemplateInUse).forEach(async variable => {
                    const selectedOption = this.optionsSelectedForTemplateInUse[variable]
                    const nodeToSet = tree.find({ data: { code: variable } })[0]
                    nodeToSet.data.checked = true
                    if (selectedOption) {
                        if (nodeToSet.data.nodeType === this.CommonSynopticTypeOptions.VARIABLE_IMAGE) {
                            nodeToSet.data.filename = JSON.parse(selectedOption).filename
                        }
                        nodeToSet.data.value = selectedOption
                    }
                    this.checkAllNodeParents(nodeToSet)
                })
            }
        },
        setAssociatedImages(image) {
            const node = this.tree?.find({ data: { code: image.nodeCode } })[0]
            if (node) {
                const filename = image.attachments[0].attachment.filename
                node.data.filename = filename
                node.data.value = JSON.stringify({
                    attachmentId: image.attachments[0].attachment.attachmentId,
                    attachmentEntityType: image.attachmentEntityType,
                    filename: filename
                })
                this.refreshImageNode++
                this.setNode(node)
            }
        },
        async setNode(node) {
            let checked = false
            if (node.data.nodeType === this.CommonSynopticTypeOptions.VARIABLE_DATE || node.data.nodeType === this.CommonSynopticTypeOptions.VARIABLE_IMAGE) {
                if (node.data.value) {
                    checked = true
                }
            } else {
                if (!isEmpty(node.data.value)) checked = true
            }
            if (this.isSelectableNode(node)) {
                document.getElementById(node.id).checked = checked
            }
            if (checked) {
                this.checkAllNodeParents(node)
                if (node.parent && node.parent.data.allowsChildrenSelection === this.CommonSynopticTypeChildSelection.SINGLE_SELECTION) {
                    this.uncheckAllNodeChildren(node.parent, node.data.code)
                }
            } else this.uncheckAllNodeParents(node)
            node.data.checked = checked
            node.data.valid = this.validNode(node)
            if (node.parent) node.parent.data.valid = !this.invalidRequiredNode(node.parent.children)
        },
        validNode(node) {
            let valid = true
            if (node.data.required) {
                if (this.isVariableNode(node)) {
                    node.data.valid = node.data.value || node.data.checked
                } else if (!node.data.checked) valid = false
            }
            return valid
        },
        validParentNode(node) {
            let valid = true
            if (node.data.required) {
                if (this.isVariableNode(node)) {
                    node.data.valid = node.data.value || node.data.checked
                } else if (!node.data.checked) valid = false
            }
            return valid
        },
        isVariableNode(node) {
            return !!(node.data.nodeType === this.CommonSynopticTypeOptions.VARIABLE_IMAGE || node.data.nodeType === this.CommonSynopticTypeOptions.VARIABLE_DATE || node.data.nodeType === this.CommonSynopticTypeOptions.VARIABLE_PERCENTAGE || node.data.nodeType === this.CommonSynopticTypeOptions.VARIABLE_TEXT)
        },
        isSelectableNode(node) {
            return !!(node.parent && node.parent.data.allowsChildrenSelection !== this.CommonSynopticTypeChildSelection.NONE)
        },
        multipleSelection(node) {
            return !!(node.parent && node.parent.data.allowsChildrenSelection === this.CommonSynopticTypeChildSelection.MULTIPLE_SELECTION)
        },
        singleSelection(node) {
            return !!(node.parent && node.parent.data.allowsChildrenSelection === this.CommonSynopticTypeChildSelection.SINGLE_SELECTION)
        },
        uncheckAllNodeParents(node) {
            if (node.parent && !node.parent.children.filter(parentNodeChild => parentNodeChild.data.checked).length) {
                node.parent.data.checked = false
                if (this.isSelectableNode(node.parent)) {
                    document.getElementById(node.parent.id).checked = false
                }
                this.uncheckAllNodeParents(node.parent)
            }
            if (node.parent) node.parent.data.valid = this.validParentNode(node.parent)
        },
        uncheckAllNodeChildren(node, nodeCode) {
            node.children.forEach(nodeChild => {
                if (nodeChild.data.code !== nodeCode) {
                    nodeChild.data.checked = false
                    nodeChild.data.valid = this.validNode(nodeChild)

                    document.getElementById(nodeChild.id).checked = false
                    if (nodeChild.hasChildren()) this.uncheckAllNodeChildren(nodeChild, nodeChild.data.code)
                }
            })
        },
        checkAllNodeParents(node) {
            if (node.parent) {
                node.parent.data.checked = true
                node.parent.data.valid = this.validNode(node.parent)
                if (this.isSelectableNode(node.parent) && document.getElementById(node.parent.id)) document.getElementById(node.parent.id).checked = true
                this.checkAllNodeParents(node.parent)
            }
        },
        onNodeUnchecked(node) {
            this.uncheckAllNodeChildren(node)
            this.uncheckAllNodeParents(node)
        },
        setMultipleSelectionNode(node) {
            node.data.valid = this.validNode(node)
            if (node.data.checked) this.checkAllNodeParents(node)
            else {
                this.uncheckAllNodeParents(node)
            }
            if (this.$refs[node.data.code] && node.data.checked) this.$refs[node.data.code].focus()
        },
        setSingleSelectionNode(node) {
            node.data.checked = !node.data.checked
            node.data.valid = this.validNode(node)
            document.getElementById(node.id).checked = node.data.checked
            if (node.data.checked) {
                this.checkAllNodeParents(node)
                if (node.parent) {
                    this.uncheckAllNodeChildren(node.parent, node.data.code)
                }
                if (this.$refs[node.data.code] && node.data.checked) {
                    this.$refs[node.data.code].focus()
                }
            } else {
                this.uncheckAllNodeParents(node)
            }
        },
        onNodeChecked(node) {
            this.checkAllNodeParents(node)
            this.tree?.checked().forEach(checkedNode => {
                if (
                    checkedNode.parent &&
                    node.parent &&
                    checkedNode !== node &&
                    checkedNode.parent.data.allowsChildrenSelection === CommonSynopticTypeChildSelection.SINGLE_SELECTION &&
                    node.parent.id === checkedNode.parent.id
                ) {
                    checkedNode.uncheck()
                }
            })
        },
        async selectTemplate(templateId) {
            this.selectedTemplateId = templateId
            await this._loadTemplateDetails()
        },
        async init() {
            this.titleFilter = ''
            await this._loadTemplates()
            if (this.templateIdInUse && this.loadCurrentTemplateDetails) {
                this.selectedTemplateId = this.templateIdInUse
                await this._loadTemplateDetails()
            }
        },
        async _loadTemplateDetails() {
            this.treeModel = null
            this.savedTreeModel = null
            this.selectedOptions = {}
            this.previewMode = false

            if (this.selectedTemplateId != null) {
                const response =
                    await this.$_toCallUnique(
                        commonSynopticTemplateService.findTemplateById(this.selectedTemplateId),
                        () => this.$_showErrorModal(this.$tc('global.description.modal.error.loadingTemplate')))
                if (response !== null) {
                    if (!this.avoidValidations) {
                        await this._loadExternalDocumentation(this.selectedTemplateId)
                    }
                    this.selectedTemplate = response

                    this.treeModel = []
                    this.selectedTemplate.tree.forEach(rootNode => {
                        this.treeModel.push(this.parseNode(rootNode))
                    })
                    this._initTree()
                }
            } else {
                this.selectedTemplate = null
            }
        },
        async _loadExternalDocumentation(templateId) {
            this.templateExternalDocumentation.hasExternalDocumentation = false
            this.templateExternalDocumentation.file = null
            this.templateExternalDocumentation.contentType = null

            const response =
                await this.$_toCallSecure(
                    commonSynopticTemplateAttachmentService.getLastExternalDocumentation(templateId),
                    error => this.$_handleAxiosError(error, [404]))
            if (response !== null) {
                this.templateExternalDocumentation.hasExternalDocumentation = true
                this.templateExternalDocumentation.file = response.data
                this.templateExternalDocumentation.contentType = response.headers['content-type']
            }
        },
        openExternalDocumentation() {
            const openFileParams = {
                blob: this.templateExternalDocumentation.file,
                type: this.templateExternalDocumentation.contentType
            }
            this.$_openFile(openFileParams)
        },
        async _loadTemplates() {
            if (!this.hideTemplatesList) {
                let [error, response] = []
                if (this.customConfig.showOnlyCompatible && this.caseId && this.sampleId) {
                    [error, response] = await this.toCall(commonSynopticTemplateService.findCompatibleIndexTemplates(this.laboratoryId, this.caseId, this.sampleId))
                } else {
                    [error, response] = await this.toCall(commonSynopticTemplateService.findAllIndexTemplates(this.laboratoryId))
                }
                if (error) {
                    this.showErrorModalGlobal(this.$t('global.description.modal.error.loadingTemplates'))
                    return
                }
                this.templates = response
                this.selectedTemplateId = null
            }
        },

        invalidRequiredNode(requiredNodes) {
            return requiredNodes.find(requiredNode => !requiredNode.data.valid)
        },
        validateTemplate(requiredNodes) {
            requiredNodes.forEach(node => {
                node.data.valid = this.validNode(node)
            })
        },
        generateSelectedOptions(checkedNodes) {
            let parsedOptions = {}
            checkedNodes.forEach(node => {
                parsedOptions[node.data.code] = node.data.value
            })
            return parsedOptions
        },
        applyTemplateAndClose() {
            const requiredNodes = this.tree?.findAll({ data: { required: true } }) ?? []
            const checkedNodes = this.tree?.findAll({ data: { checked: true } }).filter(node => this.isSelectableNode(node) || node.data.value) ?? []
            const textHtml = this.generateTextHtml()
            this.selectedOptions = cloneDeep(this.generateSelectedOptions(checkedNodes))
            if (this.avoidValidations) {
                const descriptionInfo = {
                    text: textHtml,
                    optionsSelected: this.selectedOptions,
                    templateId: this.selectedTemplateId,
                    descriptionType: DescriptionType.SYNOPTIC_TEMPLATE,
                    title: this.selectedTemplate.title,
                    textEditingAllowed: false
                }
                this.$emit('templateSelected', descriptionInfo)
                this.hide()
            } else {
                this.validateTemplate(requiredNodes)
                if (!this.invalidRequiredNode(requiredNodes)) {
                    const descriptionInfo = {
                        text: textHtml,
                        optionsSelected: this.selectedOptions,
                        templateId: this.selectedTemplateId,
                        descriptionType: DescriptionType.SYNOPTIC_TEMPLATE
                    }
                    this.$emit('templateSelected', descriptionInfo)
                    this.hide()
                } else {
                    this.showErrorModalGlobal(this.$t('studyRequest.general.requiredFieldsNotice'))
                }
            }
        },
        setSelectableNodes(liquorTreeNodes) {
            liquorTreeNodes.forEach(node => {
                node.childrenSelection = this.isSelectableNode(node)
                if (node.hasChildren()) this.setSelectableNodes(node.children)
            })
        },
        _initTree() {
            if (this.tree) {
                this.tree.setModel(this.savedTreeModel ?? this.treeModel)
                this.tree.expandAll()

                if (this.loadCurrentTemplateDetails) {
                    this.loadCustomOptionsFromParams(this.tree)
                }

                this.setSelectableNodes(this.tree.tree.model)
                this.totalNodes = this.tree.findAll({}).length
                this.totalFilteredNodeCodes = []
                this.treeFilter = ''
            }
        },
        onTreeMounted(tree) {
            this.tree = tree
            this._initTree()
        },
        parseNode(node) {
            let parsedNode = {
                text: node.text,
                data: {
                    nodeType: node.nodeType,
                    allowsChildrenSelection: node.allowsChildrenSelection,
                    code: node.code,
                    morphologicCodeId: node.morphologicCodeId,
                    topographicCodeId: node.topographicCodeId,
                    required: node.required,
                    valid: true,
                    checked: false,
                    value: null
                },
                state: { selectable: false },
                children: []
            }
            if (node.children) {
                node.children.forEach(nodeChildren => {
                    parsedNode.children.push(this.parseNode(nodeChildren))
                })
            }
            return parsedNode
        },
        nextFilteredFoundNode() {
            if (this.totalFilteredNodeCodes.length > this.currentSearchIndex + 1) {
                this.currentSearchIndex++
            } else {
                this.currentSearchIndex = 0
            }
            const nodeId = this.totalFilteredNodeCodes[this.currentSearchIndex] + 'node'
            document.getElementById(nodeId).scrollIntoView()
        },
        previousFilteredFoundNode() {
            if (this.currentSearchIndex - 1 >= 0) {
                this.currentSearchIndex--
            } else {
                this.currentSearchIndex = this.totalFilteredNodeCodes.length - 1
            }
            const nodeId = this.totalFilteredNodeCodes[this.currentSearchIndex] + 'node'
            document.getElementById(nodeId).scrollIntoView()
        },
        onTreeFiltered(foundNodes) {
            this.foundNodes = foundNodes.length
            this.totalFilteredNodeCodes = foundNodes.map(node => node.data.code)
            this.currentSearchIndex = 0
            if (!this.foundNodes.length) {
                this.tree?.expandAll()
            }
        }
    }
}
</script>

<style lang="scss">
.synoptic-modal {
    .synoptic-modal-date-picker, .synoptic-modal-number {
        width: 200px;
    }
    .synoptic-modal-picture-placeholder {
        opacity: .7;
    }
    .tree-node {
        .tree-content {
            align-items: flex-start;
            .tree-anchor {
                label {
                    font-size: font-size(very-small);
                    font-weight: bold;
                }
            }
        }
        .tree-children {
            .tree-anchor {
                label {
                    font-weight: normal;
                }
            }
        }
    }
}
.synoptic-modal li.tree-node.matched > .tree-content > span{
    color: #4680ff;
}
.synoptic-modal .search-counter {
    border-top: 1px solid grey;
    border-bottom: 1px solid grey;
}
.synoptic-modal .search-counter span {
    color: vt-color(base-text);
}
.synoptic-modal .synoptic-search input {
    border-right: 0;
}
.synoptic-modal .synoptic-search {
    border-bottom: 1px solid vt-color(general-border);
    box-shadow: 0 4px 6px -4px #9c9c9c;
}
.synoptic-modal .synoptic-search .form-control:focus,
.synoptic-modal .synoptic-search .form-control:focus ~ div div {
    border-color: vt-color(primary);
}
.synoptic-modal .synoptic-search .btn-up {
    border-right-color: transparent;
}
.synoptic-modal .synoptic-search .btn-down {
    border-left-color: transparent;
}
.synoptic-modal .synoptic-search .btn:hover {
    background: transparent;
}
.synoptic-modal .synoptic-search .btn i {
    transition: .2s;
}
.synoptic-modal .synoptic-search .btn:hover i {
    color: vt-color(primary);
}
.synoptic-modal .tree-arrow.has-child:after {
    border-color: vt-color(primary);
    transition: .3s;
}
.synoptic-modal .tree-arrow.has-child:hover:after {
    border-color: vt-color(alt-primary);
}
.synoptic-modal .tree-anchor .radio-button {
    display: flex;
}
.synoptic-modal label {
    margin-bottom: 0;
    height: auto;
}
.synoptic-modal input {
    min-width: 200px;
}
.synoptic-modal label:first-letter {
    text-transform: capitalize;
}
.synoptic-modal .tree-children {
    margin-bottom: 10px;
}
.synoptic-modal .tree-node {
    white-space: pre-wrap;
}
.synoptic-modal .tree-node .mx-datepicker {
    white-space: nowrap;
}
.synoptic-modal .tree {
    height: 100%;
}
.synoptic-modal .tree-content {
    cursor: inherit;
}
.synoptic-modal .invalid {
    color: red
}
.synoptic-modal .required-field {
    color: vt-color(red-medium) !important;
    font-style: normal;
}

.synoptic-modal .optionSelected,
.synoptic-modal .checkSelected {
  color: vt-color(primary);
}

.synoptic-modal .text-field,
.synoptic-modal .number-field {
  border-bottom: 1px solid vt-color(base-text) !important;
  border-color: transparent;
  border-radius: 0;
  padding-top: 0;
  height: 22px;
  color: vt-color(primary);
}

.synoptic-modal .text-field:focus,
.synoptic-modal .number-field:focus {
  border-bottom: 1px solid vt-color(primary) !important;
}

.synoptic-modal .text-field .validation,
.synoptic-modal .number-field .validation {
  border-bottom: 1px solid vt-color(red-medium) !important;
}

.synoptic-modal .margin-second-level {
  margin-left: 60px;
}

.synoptic-modal .margin-third-level {
  margin-left: 90px;
}

.synoptic-modal .tree-node.disabled > .tree-content .tree-checkbox {
    display: none;
}

.synoptic-modal .scrollable-area {
    overflow-y: auto;
    overflow-x: hidden;
    height: calc(100vh - 260px);
}

.synoptic-modal .template-subtitle{
  font-weight: bold;
  font-size: font-size(very-small);
}

.synoptic-modal span {
  color: vt-color(dark-text);
  font-size: font-size(very-small);
}

.synoptic-modal .custom-control-label {
  width: 100%;
}

.synoptic-modal .col-fixed {
  width: 20px;
}

.synoptic-modal .modal-body {
  padding: 0.6rem 1.1rem 0 1.1rem !important;
  overflow: hidden;
}

.synoptic-modal .field-edition {
  width: 100px;
  display: inline-block;
  font-size: 1rem;
  border: none;
  border-bottom: 1px solid grey;
  border-radius: 0;
  height: calc(0.6em + 0.5rem + 0px) !important;
  color: vt-color(primary);
}

.field-edition:focus {
  border-color: vt-color(primary);
}
img.thumbnail {
    max-width: 200px;
}
</style>

<style lang="scss" scoped>

.synoptic-templates-table {
    position: relative;
    overflow: hidden;
    margin: 1rem 0;
    border: 1px solid vt-color(general-border);
}

.browse-btn {
    p {
        height: 20px;
    }
}

.thumbnail {
    text-align: center;
}
.thumbnail > img {
    max-height: 55px;
    transform: scale(0.8);
}
.fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}
.synoptic-modal .tree-checkbox.checked:after {
    width: 3px;
    height: 8px;
    top: 0;
    left: 4px;
    transition: none;
}
.synoptic-modal .tree-checkbox {
    width: 15px;
    height: 15px;
}
.synoptic-modal .file {
  border: 1px solid rgba(0, 0, 0, 0.2);
  width: 100%;
  border-radius: 5px;
  transition: 0.2s;
  height: calc(1.5em + 0.5rem + 2px);
}

.synoptic-modal .file:hover {
  border: 1px solid vt-color(primary);
  cursor: pointer;
}

.synoptic-modal .file:hover span {
  color: vt-color(primary);
}

.synoptic-modal .file .icons {
  float: right;
  font-size: 1rem;
}

.file span {
  color: vt-color(base-text);
  font-size: 13px;
}

.template-sidepanel {
  width: 100%;
  background: #fff;
  z-index: 3;
  border-left: 1px solid #dee2e6;
  box-shadow: -5px 0px 6px 0px #9c9c9c;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.template-sidepanel .header {
  border-bottom: 1px solid #dee2e6;
}

.template-sidepanel .footer {
  border-top: 1px solid #dee2e6;
  position: absolute;
  bottom: 0;
  width: 100%;
}

.template-sidepanel .footer .btn {
  font-size: font-size(very-small);
}

.text-edition {
  color: #303133;
  text-align: justify;
  overflow-y: auto;
  flex-grow: 1;
  margin-bottom: 2rem;
}

.text-result {
  overflow-y: auto;
  height: calc(100vh - 305px);
  color: vt-color(dark-text);
}

.text-edition .title {
  font-size: font-size(normal);
  color: vt-color(dark-text);
}

.panel-visible {
  position: absolute;
  top: 0;
  right: -620px;
  height: 100%;
  width: 610px;
  transition: 0.3s;
}

.panel-visible.synoptic{
  width: 600px;
}

.active {
  right: 0;
}

.synoptic-modal {
  color: #303133;
}

.synoptic-modal .filters-header {
  border-bottom: 1px solid #dee2e6;
}

.synoptic-modal .filters-header input {
  width: 100%;
}

.synoptic-modal .filters-header span {
  font-size: 0.9rem;
}

.synoptic-modal .table-header {
  background: #f4f4f4;
  border: 1px solid #dee2e6;
  color: rgb(48, 49, 51);
}

.synoptic-modal .table-content {
  border-left: 1px solid #dee2e6;
  border-right: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
}

.synoptic-modal .table-row {
  border-bottom: 1px solid #dee2e6;
  font-size: 0.9rem;
  transition: 0.3s;
  color: rgb(48, 49, 51);
}

.synoptic-modal .table-row:hover {
  background: #f9f9f9;
  cursor: pointer;
}

.synoptic-modal .table-row .table-row-tittle {
  transition: 0.3s;
}

.synoptic-modal .table-row:hover .table-row-tittle {
  color: vt-color(primary);
}

.synoptic-modal .table-row .table-row-desc {
  text-align: justify;
}

.synoptic-modal .row-active {
  border-left: 3px solid vt-color(primary);
  background: #f9f9f9;
}

.synoptic-modal .row-active .table-row-tittle {
  color: vt-color(primary);
}

.synoptic-modal .table-content {
  height: calc(85vh - 120px);
  min-height: bp(phablet);
  overflow: auto;
}

.synoptic-modal .btn-filter {
  display: none;
}

.synoptic-modal .filter-input:focus {
  border-color: vt-color(primary);
}

.synoptic-modal .btn-filter {
  position: absolute;
  right: 5px;
  top: 0;
}
.text-edition.options-not-filled .multiselect .multiselect__tags,
.text-edition.options-not-filled .field-edition {
    border-color: vt-color(red-medium);
}
</style>
